import { decode } from 'js-base64';
import { NotificationCard } from '../../components/Card';
import {
  Avatar,
  Button,
  Center,
  Flex,
  Icon,
  Text,
  Box,
} from '@chakra-ui/react';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationLink } from '../../components/NotificationLink';
import { FiEdit } from 'react-icons/fi';
import { NotificationTime } from '../../components/Time';

import {
  ratingOptions,
  getSelectionRatingStyle,
} from '@/constants/scorecard-configs';
import _ from 'lodash';

const ReviewCompleted = ({ notification = {}, onClose = () => {} }) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { custom_attributes } = notification;
  const data = JSON.parse(decode(custom_attributes));

  const {
    review,
    job,
    contact,
    scorecard_url,
    reviewer = {},
    job_stage,
    scorecard_rating = {},
  } = data;

  const { name: review_name } = review;
  const { name: reviewer_name } = reviewer;
  const { id: contact_id, name: contact_name, avatar_url } = contact;
  const { url: job_url, name: job_name } = job;
  const { name: job_stage_name } = job_stage;

  const selectedRating = _.find(ratingOptions, {
    value: scorecard_rating,
  });

  const styleProps = getSelectionRatingStyle(selectedRating);
  const label = selectedRating?.label;
  const IconComponent = selectedRating?.icon;

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center minW="46px" h="46px" borderRadius="full" bg="ats-tertiary">
        <Icon as={FiEdit} color="ats-primary" />
      </Center>
      <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start">
        {label ? (
          <Text fontSize="sm">
            <Text as="span" size="sm-semi-bold">
              {review_name}
            </Text>
            <Text as="span" ml="1" mr="1">
              feedback submitted:
            </Text>
            <Text as="span" size="sm-semi-bold">
              <NotificationLink
                to={`/contacts/${contact_id}`}
                label={contact_name}
                onClose={onClose}
                notification={notification}
              />
            </Text>
            <Text as="span" ml="1" mr="1">
              -
            </Text>
            <Text as="span" size="sm-semi-bold">
              {job_name}
            </Text>
          </Text>
        ) : (
          <Text>
            <Text as="span" size="sm-semi-bold">
              {reviewer_name}
            </Text>
            <Text as="span" fontSize="sm">
              {' '}
              has submitted a scorecard for candidate{' '}
            </Text>
            <Text as="span" size="sm-semi-bold">
              <NotificationLink
                to={`/contacts/${contact_id}`}
                label={contact_name}
                onClose={onClose}
                notification={notification}
              />
            </Text>
            <Text as="span" fontSize="sm">
              {' '}
              on Job{' '}
            </Text>
            <Text as="span" size="sm-semi-bold">
              <NotificationLink
                to={job_url}
                label={job_name}
                onClose={onClose}
                notification={notification}
              />
            </Text>
          </Text>
        )}

        <Text size="sm-semi-bold">Details</Text>
        <Flex
          border="1px solid"
          borderColor="border-color"
          borderRadius="sm"
          p="14px"
          flexDir="column"
          gap="6px"
          mt="-6px"
        >
          <Text size="sm-semi-bold">Candidate</Text>
          <Flex mb="6px" gap="6px">
            <Avatar
              size="2xs"
              borderRadius="4px"
              className="avatar-xs"
              src={avatar_url}
              name={contact_name}
            />
            <Text fontSize="sm">{contact_name}</Text>
          </Flex>
          <Text size="sm-semi-bold">Job</Text>
          <Text fontSize="sm" mb="6px">
            {job_name}
          </Text>
          <Text size="sm-semi-bold">Stage</Text>
          <Text fontSize="sm" mb="6px">
            {job_stage_name}
          </Text>
          <Text size="sm-semi-bold">Activity</Text>
          <Text fontSize="sm" mb="6px">
            {review_name}
          </Text>
          {selectedRating && (
            <Box py="2">
              <Text size="sm-semi-bold">Overall evaluation</Text>
              <Flex gap="6px">
                {IconComponent && (
                  <Icon
                    as={IconComponent}
                    h={styleProps.h || '24px'}
                    w={styleProps.w || '24px'}
                    color={styleProps?.color}
                  />
                )}
                <Text fontSize="sm">{label}</Text>
              </Flex>
            </Box>
          )}
        </Flex>
        <NotificationTime notification={notification} />
        <Button
          w="fit-content"
          variant="primary"
          borderRadius="8px"
          onClick={() => {
            markAsRead({ id: notification?.id });
            window.open(scorecard_url, '_self');
            onClose();
          }}
          maxH="34px"
          data-test-id="view-scorecard-button"
        >
          View scorecard
        </Button>
      </Flex>
    </Flex>
  );
};

export const ReviewCompletedNotification = ({
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <ReviewCompleted notification={notification} onClose={onClose} />
    </NotificationCard>
  );
};
